import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Info } from 'lucide-react';
import { adminCommissionTiersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

// Escalones marginales por volumen diario: la N-ésima venta del día paga el
// monto del escalón donde cae N. Editarlos solo afecta ventas futuras.
const CommissionTiersEditor = () => {
  const [tiers, setTiers] = useState([]);
  const [mode, setMode] = useState('retroactive');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    adminCommissionTiersAPI
      .getAll()
      .then((res) => {
        if (res?.success) {
          setTiers((res.data || []).map((t) => ({
            min_count: t.min_count,
            max_count: t.max_count ?? '',
            amount: t.amount,
          })));
          if (res.mode) setMode(res.mode);
        }
      })
      .catch(() => {
        toast({ title: 'Error', description: 'No se pudieron cargar los escalones', variant: 'destructive' });
      })
      .finally(() => setLoading(false));
  }, []);

  const changeMode = async (next) => {
    const previous = mode;
    setMode(next);
    try {
      const res = await adminCommissionTiersAPI.updateMode(next);
      if (res?.success) toast({ title: 'Modo actualizado', description: res.message });
    } catch (err) {
      setMode(previous);
      toast({ title: 'Error', description: 'No se pudo cambiar el modo', variant: 'destructive' });
    }
  };

  // Ejemplo con los escalones actuales para que el cambio de modo sea tangible.
  const example = (() => {
    const second = tiers[1];
    if (!second?.min_count || !second?.amount) return null;
    const n = Number(second.min_count);
    const rate = Number(second.amount);
    const first = tiers[0];
    const marginalTotal =
      (Number(first?.min_count) ? (n - 1) * Number(first.amount || 0) : 0) + rate;
    return {
      n,
      retroactive: n * rate,
      marginal: marginalTotal,
      rate,
    };
  })();

  const updateTier = (index, field, value) => {
    setTiers(tiers.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    const nextMin = last?.max_count ? Number(last.max_count) + 1 : (Number(last?.min_count) || 0) + 1;
    setTiers([...tiers, { min_count: nextMin, max_count: '', amount: '' }]);
  };

  const removeTier = (index) => setTiers(tiers.filter((_, i) => i !== index));

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      const payload = tiers.map((t) => ({
        min_count: Number(t.min_count),
        max_count: t.max_count === '' || t.max_count === null ? null : Number(t.max_count),
        amount: Number(t.amount),
      }));
      const res = await adminCommissionTiersAPI.update(payload);
      if (res?.success) {
        toast({ title: 'Escalones actualizados', description: res.message });
        setTiers((res.data || []).map((t) => ({
          min_count: t.min_count,
          max_count: t.max_count ?? '',
          amount: t.amount,
        })));
      }
    } catch (err) {
      setError(err?.message || 'No se pudieron guardar los escalones');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', height: 40, borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.1)', padding: '0 10px',
    fontSize: 14, color: '#0A0A0A', background: '#fff', outline: 'none',
  };

  return (
    <div style={{ padding: 24, fontFamily: 'Inter, system-ui, sans-serif', maxWidth: 720 }}>
      <Toaster />

      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em' }}>
          Escalones de comisión
        </h2>
        <p style={{ fontSize: 13, color: '#6B7280', marginTop: 3 }}>
          Entre más cierra un closer en el día, más gana por cada activación.
        </p>
      </div>

      {/* Modo de cálculo */}
      <div style={{
        background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 20,
        padding: '18px 20px', marginBottom: 12,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0A0A0A' }}>Cómo se aplica el escalón</div>
        <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3, marginBottom: 14 }}>
          Define qué pasa con las ventas anteriores del día cuando el closer sube de escalón.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10 }}>
          {[
            {
              key: 'retroactive',
              title: 'Retroactivo',
              desc: 'Al subir de escalón, TODAS las ventas del día pasan a la tarifa nueva.',
              amount: example?.retroactive,
            },
            {
              key: 'marginal',
              title: 'Marginal',
              desc: 'Cada venta conserva la tarifa del escalón donde cayó su posición.',
              amount: example?.marginal,
            },
          ].map((opt) => {
            const active = mode === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => changeMode(opt.key)}
                style={{
                  textAlign: 'left', cursor: 'pointer', borderRadius: 16, padding: '14px 16px',
                  border: active ? 'none' : '1px solid rgba(0,0,0,0.08)',
                  background: active ? '#0A0A0A' : '#fff',
                  color: active ? '#fff' : '#0A0A0A',
                }}>
                <div style={{ fontSize: 13.5, fontWeight: 700 }}>{opt.title}</div>
                <div style={{ fontSize: 12, opacity: active ? 0.7 : 0.55, marginTop: 4, lineHeight: 1.5 }}>
                  {opt.desc}
                </div>
                {example && opt.amount != null && (
                  <div style={{
                    marginTop: 9, paddingTop: 9,
                    borderTop: `1px solid ${active ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)'}`,
                    fontSize: 12, fontWeight: 600,
                  }}>
                    Con {example.n} ventas en un día: ${Number(opt.amount).toLocaleString('es-MX')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{
        background: 'rgba(0,0,0,0.03)', borderRadius: 14, padding: '13px 15px',
        display: 'flex', gap: 9, marginBottom: 16,
      }}>
        <Info size={15} color="#6B7280" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.55 }}>
          El conteo se reinicia cada día (hora de México). Los cambios de escalones y de modo
          <strong> solo aplican a ventas futuras</strong>: las comisiones ya generadas quedan congeladas
          con el monto que tenían al momento de la venta.
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '32px 0', color: '#9CA3AF', fontSize: 14 }}>Cargando...</div>
      ) : (
        <div style={{
          background: '#fff', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 20, padding: '20px 22px',
        }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 40px', gap: 10, marginBottom: 8,
          }}>
            {['Desde (venta #)', 'Hasta (venta #)', 'Pago por venta', ''].map((h) => (
              <div key={h} style={{
                fontSize: 11, fontWeight: 600, color: '#9CA3AF',
                textTransform: 'uppercase', letterSpacing: '0.06em',
              }}>
                {h}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tiers.map((tier, index) => (
              <div key={index} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 40px', gap: 10, alignItems: 'center' }}>
                <input
                  type="number"
                  min="1"
                  value={tier.min_count}
                  onChange={(e) => updateTier(index, 'min_count', e.target.value)}
                  style={inputStyle}
                />
                <input
                  type="number"
                  min="1"
                  value={tier.max_count}
                  onChange={(e) => updateTier(index, 'max_count', e.target.value)}
                  placeholder={index === tiers.length - 1 ? 'Sin tope' : ''}
                  style={inputStyle}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={tier.amount}
                  onChange={(e) => updateTier(index, 'amount', e.target.value)}
                  placeholder="$"
                  style={inputStyle}
                />
                <button
                  onClick={() => removeTier(index)}
                  disabled={tiers.length === 1}
                  title="Quitar escalón"
                  style={{
                    width: 40, height: 40, borderRadius: 10,
                    border: '1px solid rgba(0,0,0,0.08)', background: '#fff',
                    cursor: tiers.length === 1 ? 'default' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: tiers.length === 1 ? 0.4 : 1,
                  }}>
                  <Trash2 size={14} color="#9CA3AF" />
                </button>
              </div>
            ))}
          </div>

          <button
            onClick={addTier}
            style={{
              width: '100%', marginTop: 12, height: 42, borderRadius: 12,
              border: '1px dashed rgba(0,0,0,0.18)', background: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontSize: 13.5, fontWeight: 600, color: '#6B7280',
            }}>
            <Plus size={15} /> Agregar escalón
          </button>

          {error && (
            <div style={{
              marginTop: 14, background: '#FEF2F2', border: '1px solid #FECACA',
              borderRadius: 12, padding: '11px 14px', fontSize: 13, color: '#B91C1C',
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: '100%', marginTop: 14, height: 46, borderRadius: 9999,
              border: 'none', background: '#0A0A0A', color: '#fff',
              fontSize: 14.5, fontWeight: 600,
              cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1,
            }}>
            {saving ? 'Guardando...' : 'Guardar escalones'}
          </button>
        </div>
      )}
    </div>
  );
};

export default CommissionTiersEditor;
